import TokenExpired from 'areas/404/components/TokenExpired';
import TokenMissing from 'areas/404/components/TokenMissing';
import { DomainContext } from 'common/layouts/LayoutContext';
import { isUserAuthenticated } from 'common/selectors/auth';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Icon, Modal, Form } from 'semantic-ui-react';
import { useToasts } from 'common/toast';
import { getCurrentUser } from 'aws-amplify/auth';
import LoadingSpinner from '../../../common/components/LoadingSpinner';
import { PageSection, Title } from '../../../common/styles/page';
import { useApiAdminV1RepositoryRepositoryFileShareRetrieveByTokenRetrieveQuery } from '../../../store/generated/admin/repository-file-share';
import { FileTypes } from 'common/constants/filetypes';
import { getYouTubeEmbedUrl } from 'common/utils/resourceLinks';
import FileViewer from '../../settings/repository-management/components/FileViewer';
import { useVoteFileUploadMutation } from '../../settings/repository-management/api/repositoryApi';
import contentSharingSlice from '../contentSharingSlice';

type RepositoryFileShareWithVoteMetadata = {
  upvote_count?: number;
  downvote_count?: number;
  your_vote?: 1 | -1 | null;
};

const ContentShareLayout = () => {
  const isAuthenticated = useSelector(isUserAuthenticated);
  const [searchParams] = useSearchParams();
  const parameterToken = searchParams.get('token');
  const [isExpiredAccess, setIsExpiredAccess] = useState(false);
  const [isTokenMissing, setIsTokenMissing] = useState(!parameterToken);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [amplifyAuthenticated, setAmplifyAuthenticated] = useState(false);
  const [pdfViewMode, setPdfViewMode] = useState<'scale' | 'fitWidth' | 'fullPage'>('fitWidth');
  const [pdfScale, setPdfScale] = useState(1);

  const Domain = useContext(DomainContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Check Amplify auth state on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        await getCurrentUser();
        setAmplifyAuthenticated(true);
      } catch {
        setAmplifyAuthenticated(false);
      } finally {
        setCheckingAuth(false);
      }
    };
    checkAuth();
  }, []);

  const handleTokenChange = useCallback(
    (token: string) => {
      dispatch(contentSharingSlice.actions.setToken(token));
    },
    [dispatch]
  );

  useEffect(() => {
    if (parameterToken) {
      handleTokenChange(parameterToken);
    }
  }, [handleTokenChange, parameterToken]);

  const [isVotePending, setIsVotePending] = useState(false);
  const [submittedVote, setSubmittedVote] = useState<1 | -1 | null>(null);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [feedbackNotes, setFeedbackNotes] = useState('');
  const { addToast } = useToasts();
  const [voteFileUpload] = useVoteFileUploadMutation();

  const { data, isError, error, isLoading, refetch } =
    useApiAdminV1RepositoryRepositoryFileShareRetrieveByTokenRetrieveQuery(
      { token: parameterToken! },
      { skip: !parameterToken }
    ) as {
      data?: RepositoryFileShareWithVoteMetadata & {
        id: number;
        name?: string;
        description?: string | null;
        object_key?: string | null;
        type?: number;
        upvote_count?: number;
        downvote_count?: number;
        your_vote?: 1 | -1 | null;
      };
      isError: boolean;
      error?: any;
      isLoading: boolean;
      refetch?: () => void;
    };

  const submitGuestVote = async (vote: 1 | -1, notes?: string) => {
    if (!data?.id || !parameterToken || isVotePending) {
      return;
    }

    setIsVotePending(true);
    try {
      await voteFileUpload({
        id: data.id,
        vote,
        feedback_notes: notes,
        guestToken: parameterToken,
      }).unwrap();
      setSubmittedVote(vote);
      if (refetch) {
        refetch();
      }
      addToast('Thanks for your feedback!', {
        appearance: 'success',
        autoDismiss: true,
      });
    } catch {
      // Intentionally ignore vote errors for simplicity.
    } finally {
      setIsVotePending(false);
    }
  };

  const handleGuestVoteClick = async (vote: 1 | -1) => {
    if (isVotePending) {
      return;
    }

    if (vote === -1) {
      setFeedbackNotes('');
      setIsFeedbackModalOpen(true);
      return;
    }

    await submitGuestVote(1);
  };

  const handleSubmitFeedback = async () => {
    setIsFeedbackModalOpen(false);
    await submitGuestVote(-1, feedbackNotes || undefined);
  };

  useEffect(() => {
    if (data?.id) {
      setPdfViewMode('fitWidth');
      setPdfScale(1);
    }
  }, [data?.id]);

  useEffect(() => {
    if (isError) {
      const status = (error as any)?.status;
      if (status === 403) {
        setIsExpiredAccess(true);
      } else if (status === 400) {
        setIsTokenMissing(true);
      } else {
        navigate('/404');
      }
    }
  }, [isError, error, navigate]);

  if (isTokenMissing) return <TokenMissing />;
  if (checkingAuth || isLoading) return <LoadingSpinner />;
  if (isExpiredAccess) return <TokenExpired />;

  // Use Amplify auth state OR Redux auth state (Amplify is more reliable for public routes)
  // For the sidebar to work, we need BOTH Amplify AND Redux user context initialized
  const userIsAuthenticated = amplifyAuthenticated && isAuthenticated;

  // Check if we're in the /app path (authenticated layout with AuthLayout wrapper)
  const isInAuthLayout = location.pathname.startsWith('/app/');

  // For authenticated users within /app/content, show content directly (AuthLayout provides sidebar/topbar)
  if (userIsAuthenticated && isInAuthLayout) {
    return (
      <AuthenticatedContentWrapper>
        <PageSection
          display="flex"
          flexDirection="column"
          width="100%"
          style={{ paddingTop: '20px', paddingBottom: '30px' }}
        >
          <Title style={{ paddingBottom: '10px' }}>{data?.name || 'Content Viewer'}</Title>
          {data?.description && (
            <Description>
              <div style={{ display: 'inline', cursor: 'default' }} title={data.description}>
                {data.description.length > 300
                  ? `${data.description.slice(0, 300)}... `
                  : data.description}
              </div>
            </Description>
          )}
        </PageSection>
        
        {data && parameterToken && (
          data.type === FileTypes.URL && data.object_key ? (
            (() => {
              const embedUrl = getYouTubeEmbedUrl(data.object_key);
              if (embedUrl) {
                return (
                  <FileViewerWrapper>
                    <YoutubeEmbedWrapper>
                      <YoutubeIframe
                        src={embedUrl}
                        title={data.name || 'Shared video'}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                      />
                    </YoutubeEmbedWrapper>
                  </FileViewerWrapper>
                );
              }

              return (
                <FileViewerWrapper>
                  <LinkPreviewWrapper>
                    <LinkPreviewIcon>
                      <Icon name="external alternate" size="huge" color="blue" />
                    </LinkPreviewIcon>
                    <LinkPreviewContent>
                      <LinkPreviewHeading>Shared culture link</LinkPreviewHeading>
                      <LinkPreviewText>
                        Open the shared resource to check it out and learn more!
                      </LinkPreviewText>
                      <LinkPreviewButton
                        href={data.object_key}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open shared link
                        <Icon name="external alternate" />
                      </LinkPreviewButton>
                      <LinkPreviewUrl
                        href={data.object_key}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={data.object_key}
                      >
                        {data.object_key}
                      </LinkPreviewUrl>
                    </LinkPreviewContent>
                  </LinkPreviewWrapper>
                </FileViewerWrapper>
              );
            })()
          ) : (
            <FileViewerWrapper>
              <FileViewer
                fileId={data.id.toString()}
                isFullScreen={true}
                guestToken={parameterToken}
                viewMode={data.type === FileTypes.PDF ? pdfViewMode : undefined}
                scale={data.type === FileTypes.PDF ? pdfScale : undefined}
                onViewModeChange={mode => setPdfViewMode(mode)}
                onScaleChange={setPdfScale}
              />
            </FileViewerWrapper>
          )
        )}
      </AuthenticatedContentWrapper>
    );
  }

  // For guest users or authenticated users on /content (not /app/content), show minimal header with login button
  return (
    <GuestFullPageWrapper>
      <GuestHeader>
        <LogoSection>
          <a href="https://www.nuwaveco.com/" target="_blank" rel="noopener noreferrer">
            <IMG src={Domain.fullImage} alt="Logo" />
          </a>
        </LogoSection>
        <GuestTitleSection>
          <GuestPageTitle>
            {data?.name || ''}
          </GuestPageTitle>
          {data?.description && (
            <GuestHeaderDescription title={data.description}>
              {data.description.length > 220 ? `${data.description.slice(0, 220)}...` : data.description}
            </GuestHeaderDescription>
          )}
        </GuestTitleSection>
        <LoginSection>
          <Button
            icon
            size="large"
            color={submittedVote === 1 || data?.your_vote === 1 ? 'green' : undefined}
            onClick={() => handleGuestVoteClick(1)}
            disabled={isVotePending}
            loading={isVotePending}
            title="Helpful"
            style={{ minWidth: 32, minHeight: 32 }}
          >
            <Icon name="thumbs up" />
          </Button>
          <Button
            icon
            size="large"
            color={submittedVote === -1 || data?.your_vote === -1 ? 'red' : undefined}
            onClick={() => handleGuestVoteClick(-1)}
            disabled={isVotePending}
            loading={isVotePending}
            title="Not helpful"
            style={{ minWidth: 32, minHeight: 32, marginLeft: 12 }}
          >
            <Icon name="thumbs down" />
          </Button>
        </LoginSection>
      </GuestHeader>

      <Modal
        open={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
        size="small"
        closeIcon
        dimmer={{ style: { zIndex: 20000 } }}
        style={{ zIndex: 20001, top: '50%', transform: 'translateY(-50%)' }}
      >
        <Modal.Header>Tell us more</Modal.Header>
        <Modal.Content>
          <div style={{ marginBottom: 16 }}>
            We're sorry this content wasn't helpful. Please share more detail so we can improve future recommendations.
          </div>
          <Form>  
            <Form.TextArea
              placeholder="Optional feedback notes..."
              value={feedbackNotes}
              onChange={(_, data) => setFeedbackNotes(data.value as string)}
              style={{ minHeight: 150 }}
            />
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setIsFeedbackModalOpen(false)} disabled={isVotePending}>
            Cancel
          </Button>
          <Button
            primary
            loading={isVotePending}
            onClick={handleSubmitFeedback}
          >
            Submit feedback
          </Button>
        </Modal.Actions>
      </Modal>

      <GuestContentWrapper>
        {data && parameterToken && (
          data.type === FileTypes.URL && data.object_key ? (
            (() => {
              const embedUrl = getYouTubeEmbedUrl(data.object_key);
              if (embedUrl) {
                return (
                  <FileViewerWrapper>
                    <YoutubeEmbedWrapper>
                      <YoutubeIframe
                        src={embedUrl}
                        title={data.name || 'Shared video'}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                      />
                    </YoutubeEmbedWrapper>
                  </FileViewerWrapper>
                );
              }

              return (
                <FileViewerWrapper>
                  <LinkPreviewWrapper>
                    <LinkPreviewIcon>
                      <Icon name="external alternate" size="huge" color="blue" />
                    </LinkPreviewIcon>
                    <LinkPreviewContent>
                      <LinkPreviewHeading>Shared culture link</LinkPreviewHeading>
                      <LinkPreviewText>
                        Open the shared resource to check it out and learn more!
                      </LinkPreviewText>
                      <LinkPreviewButton
                        href={data.object_key}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open shared link
                        <Icon name="external alternate" />
                      </LinkPreviewButton>
                      <LinkPreviewUrl
                        href={data.object_key}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={data.object_key}
                      >
                        {data.object_key}
                      </LinkPreviewUrl>
                    </LinkPreviewContent>
                  </LinkPreviewWrapper>
                </FileViewerWrapper>
              );
            })()
          ) : (
            <FileViewerWrapper>
              <FileViewer
                fileId={data.id.toString()}
                isFullScreen={true}
                guestToken={parameterToken}
                viewMode={data.type === FileTypes.PDF ? pdfViewMode : undefined}
                scale={data.type === FileTypes.PDF ? pdfScale : undefined}
                onViewModeChange={mode => setPdfViewMode(mode)}
                onScaleChange={setPdfScale}
              />
            </FileViewerWrapper>
          )
        )}
      </GuestContentWrapper>
    </GuestFullPageWrapper>
  );
};

export default ContentShareLayout;

// Styled components
const AuthenticatedContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px 40px 60px 40px;
  height: 100%;
`;

const GuestFullPageWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background-color: #fff;
  z-index: 9999;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const GuestContentWrapper = styled.div`
  width: 90vw;
  max-width: 1400px;
  margin: 0 auto;
  transition: all 0.5s;
  padding: 104px 5% 24px;
  min-height: calc(100vh - 104px);
`;

const GuestTitleSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  flex: 1 1 66%;
  max-width: 66%;
  min-width: 0;
  margin: 0 24px 0 0;
  overflow: hidden;

  @media (max-width: 900px) {
    flex: 1 1 100%;
    max-width: 100%;
    margin: 0 16px 0 0;
  }
`;

const GuestPageTitle = styled(Title)`
  margin: 0;
  padding-bottom: 4px;
  font-size: 1.25rem;
  line-height: 1.2;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Description = styled.div`
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #777;
`;

const GuestHeaderDescription = styled(Description)`
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.4;
  width: 100%;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
`;

const FileViewerWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 5% 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LinkPreviewWrapper = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 28px 26px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.14), rgba(14, 165, 233, 0.06));
  border: 1px solid rgba(96, 165, 250, 0.2);
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(10, 71, 134, 0.07);
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 24px;
  align-items: center;
`;

const LinkPreviewIcon = styled.div`
  width: 84px;
  height: 84px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 24px;
  background: rgba(59, 130, 246, 0.12);
`;

const LinkPreviewContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

const LinkPreviewHeading = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
`;

const LinkPreviewText = styled.div`
  color: #334155;
  font-size: 1rem;
  line-height: 1.6;
`;

const LinkPreviewButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  border-radius: 14px;
  background: #eff6ff;
  color: #1d4ed8;
  font-weight: 600;
  text-decoration: none;
  width: fit-content;

  &:hover {
    text-decoration: underline;
  }
`;

const LinkPreviewUrl = styled.a`
  color: #0969da;
  font-size: 0.95rem;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  text-decoration: none;
  min-width: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const GuestHeader = styled.header`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  padding: 16px 5%;
  background: white;
  border-bottom: 1px solid #e0e0e0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  min-height: 80px;
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  margin-right: 24px;
`;

const LoginSection = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
`;

const IMG = styled.img`
  height: 50px;
`;

const YoutubeEmbedWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  background: #000;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
`;

const YoutubeIframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
`;
